import { createPinia, type StateTree } from 'pinia'
import { type Component, createApp, createSSRApp } from 'vue'
import { createMemoryHistory, createRouter, createWebHistory, type Router } from 'vue-router'
import { createI18n, type LocaleMessages } from '../i18n'
import { createLocation } from '../location'
import { findComponent } from '../util'

declare module 'vue' {
  interface App {
    getRouter: () => Router
    getState: () => Record<string, StateTree>
    getComponent: (name: string, id?: string) => Component | undefined
  }
}

export type BootstrapOptions = {
  messages: LocaleMessages
  base?: string
  language?: string
  url?: string | URL
  ssr?: boolean
}

export const bootstrapApp = async (root: Component, options: BootstrapOptions) => {
  // Decided here, not from the module-level constant: a test that installs a DOM for a while would otherwise pin every later app to web history.
  const ssr = options.ssr ?? typeof window === 'undefined'
  const app = ssr ? createSSRApp(root) : createApp(root)

  const pinia = createPinia()
  const hydrated = (globalThis as { state?: Record<string, StateTree> }).state
  if (hydrated) {
    pinia.state.value = hydrated
  }

  const router = createRouter({
    history: ssr ? createMemoryHistory(options.base) : createWebHistory(options.base),
    routes: []
  })

  const i18n = await createI18n(options.messages, options.language)

  app
    .use(pinia)
    .use(router)
    .use(i18n)

    .use(createLocation(options.url))

  app.getRouter = () => router
  app.getState = () => pinia.state.value
  app.getI18n = () => i18n
  app.getComponent = (name, id) => findComponent(key => app.component(key), name, id)

  return app
}
